import { useCallback, useEffect, useState } from 'react';

import {
  anwenden,
  gespeichertesFarbschema,
  speichern,
  type Farbschema,
} from '../lib/farbschema';

/**
 * Haelt die Wahl des Farbschemas und traegt sie an das Wurzelelement.
 *
 * Der Zustand liegt bewusst **nicht** in einem globalen Speicher: Es gibt genau
 * eine Bedienstelle (die Fusszeile der Seitenleiste), und das Attribut am
 * Wurzelelement ist ohnehin die Wahrheit fuer die Darstellung.
 *
 * Bei `system` wird auf Aenderungen der Systemeinstellung gehoert - wer sein
 * Betriebssystem abends umstellt, soll die Anwendung nicht neu laden muessen.
 */
export function useFarbschema() {
  const [schema, setSchemaZustand] = useState<Farbschema>(gespeichertesFarbschema);

  useEffect(() => {
    anwenden(schema);
    if (schema !== 'system') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
    const beiWechsel = () => anwenden('system');
    abfrage.addEventListener('change', beiWechsel);
    return () => abfrage.removeEventListener('change', beiWechsel);
  }, [schema]);

  const setSchema = useCallback((neu: Farbschema) => {
    speichern(neu);
    // **Vor** dem Zustandswechsel, nicht erst im Effekt danach. Ein Effekt
    // laeuft nach dem Durchgang, den die Umschaltung ausloest - alles, was
    // seine Farben beim Aufbau aus dem Attribut liest, bekaeme dabei noch den
    // alten Wert. Das betrifft die Diagramme: ECharts malt auf eine Leinwand
    // und kennt keine CSS-Variablen, `diagrammFarben()` liest deshalb das
    // Attribut. Und weil das Attribut kein React-Zustand ist, loest das
    // spaetere Setzen keinen weiteren Durchgang aus - das Diagramm bliebe in
    // den alten Farben stehen. Gemeldet im Codex-Review zu PR #71.
    anwenden(neu);
    setSchemaZustand(neu);
  }, []);

  return { schema, setSchema };
}

export default useFarbschema;

/**
 * Meldet, ob gerade das dunkle Schema gilt - und **loest einen neuen Durchgang
 * aus, wenn sich das aendert.**
 *
 * Fuer alles, was seine Farben nicht aus CSS bezieht, sondern beim Aufbau aus
 * dem Attribut liest: die Diagramme. ECharts malt auf eine Leinwand und kennt
 * keine CSS-Variablen.
 *
 * Warum ein `MutationObserver` und nicht der Zustand aus `useFarbschema`: Das
 * Attribut aendert sich auch **ohne** React-Zustandswechsel - naemlich dann,
 * wenn "System" gewaehlt ist und das Betriebssystem umschaltet. Dieser Weg
 * bekommt beide Faelle mit, unabhaengig davon, wo die Umschaltung herkommt.
 */
export function useIstDunkel(): boolean {
  const [dunkel, setDunkel] = useState(
    () => typeof document !== 'undefined'
      && document.documentElement.getAttribute('data-farbschema') === 'dunkel',
  );

  useEffect(() => {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return undefined;
    const wurzel = document.documentElement;
    const pruefen = () => setDunkel(wurzel.getAttribute('data-farbschema') === 'dunkel');
    pruefen();
    const beobachter = new MutationObserver(pruefen);
    beobachter.observe(wurzel, { attributes: true, attributeFilter: ['data-farbschema'] });
    return () => beobachter.disconnect();
  }, []);

  return dunkel;
}
